import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight, FiMessageSquare, FiTool, FiSearch, FiFileText, FiPhone,
  FiMapPin, FiBarChart2, FiInfo, FiFolder, FiBookOpen,
  FiCheckCircle, FiUsers,
} from 'react-icons/fi';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { useBranding } from '../../hooks/useBranding';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

interface ItemPublico { titulo: string; descricao: string; href: string; icone: ReactNode; modulo?: string }

/** Serviços que o cidadão usa SEM precisar de login. `modulo` (quando definido) só mostra se ativo. */
const SEM_LOGIN: ItemPublico[] = [
  { titulo: 'Ouvidoria', descricao: 'Sugestão, reclamação, denúncia ou elogio — identificada, sigilosa ou anônima.', href: '/ouvidoria', icone: <FiMessageSquare size={20} />, modulo: 'ouvidoria' },
  { titulo: 'Zeladoria urbana', descricao: 'Buraco, iluminação, limpeza e outros problemas — pode ser anônima.', href: '/zeladoria', icone: <FiTool size={20} />, modulo: 'zeladoria' },
  { titulo: 'Acompanhar protocolo', descricao: 'Consulte o andamento pelo número e código do seu protocolo.', href: '/protocolo', icone: <FiSearch size={20} /> },
  { titulo: 'Carta de Serviços', descricao: 'Veja todos os serviços da prefeitura, requisitos e prazos.', href: '/servicos', icone: <FiFileText size={20} /> },
  { titulo: 'Canais de atendimento', descricao: 'Telefones, endereços e horários de atendimento.', href: '/canais', icone: <FiPhone size={20} /> },
  { titulo: 'Mapa de demandas', descricao: 'Veja no mapa as manifestações registradas pela cidade.', href: '/mapa', icone: <FiMapPin size={20} /> },
];

/** Transparência pública — dados abertos, sem login (item 2.78). */
const TRANSPARENCIA: ItemPublico[] = [
  { titulo: 'Ouvidoria', descricao: 'Indicadores da Lei 13.460/2017.', href: '/transparencia/ouvidoria', icone: <FiBarChart2 size={18} />, modulo: 'ouvidoria' },
  { titulo: 'e-SIC (LAI)', descricao: 'Pedidos de acesso à informação.', href: '/transparencia/esic', icone: <FiInfo size={18} />, modulo: 'esic' },
  { titulo: 'Zeladoria', descricao: 'Demandas urbanas por tipo e bairro.', href: '/transparencia/zeladoria', icone: <FiTool size={18} />, modulo: 'zeladoria' },
  { titulo: 'Atos oficiais', descricao: 'Decretos, portarias e demais atos.', href: '/transparencia/atos-oficiais', icone: <FiFolder size={18} />, modulo: 'ato_oficial' },
  { titulo: 'Protocolo legislativo', descricao: 'Proposições e tramitação legislativa.', href: '/transparencia/protocolo-legislativo', icone: <FiBookOpen size={18} />, modulo: 'protocolo_legislativo' },
  { titulo: 'Diário oficial', descricao: 'Edições publicadas do diário.', href: '/transparencia/diario-oficial', icone: <FiBookOpen size={18} />, modulo: 'diario_oficial' },
];

interface Numeros {
  ouvidoria: number | null;
  esic: number | null;
  zeladoria: number | null;
}

interface VagaResumo { id: string; titulo: string; descricao: string | null; total_vagas: number; encerra_em: string | null }

export function HomePublicaPage() {
  const branding = useBranding();
  const modulosAtivos = useModulosAtivos();
  // Mostra o item se não depende de módulo, ou se o módulo está ativo. Enquanto
  // carrega (null), esconde os que dependem de módulo (evita exibir desativados).
  const visivel = (m?: string) => !m || (!!modulosAtivos && modulosAtivos.has(m));
  const semLogin = SEM_LOGIN.filter((s) => visivel(s.modulo));
  const transparencia = TRANSPARENCIA.filter((s) => visivel(s.modulo));
  const [numeros, setNumeros] = useState<Numeros>({ ouvidoria: null, esic: null, zeladoria: null });
  const [vagasConselho, setVagasConselho] = useState<VagaResumo[]>([]);

  useEffect(() => {
    // Vagas do conselho abertas — leitura pública (transparência, Lei 13.460).
    supabase.from('conselho_vagas')
      .select('id, titulo, descricao, total_vagas, encerra_em')
      .eq('estabelecimento_id', getEstabelecimentoAtualId())
      .eq('aberta', true)
      .order('criado_em', { ascending: false })
      .then(({ data }) => {
        const hoje = new Date().toISOString().slice(0, 10);
        setVagasConselho(((data ?? []) as VagaResumo[]).filter((v) => !v.encerra_em || v.encerra_em >= hoje));
      });
  }, []);

  useEffect(() => {
    const id = getEstabelecimentoAtualId();
    const ano = new Date().getFullYear();
    Promise.allSettled([
      supabase.rpc('ouvidoria_indicadores_publico', { p_estabelecimento_id: id, p_ano: ano }),
      supabase.rpc('esic_indicadores_publico', { p_estabelecimento_id: id, p_ano: ano }),
      supabase.rpc('zeladoria_indicadores_publicos', { p_estabelecimento_id: id, p_ano: ano }),
    ]).then(([ouv, esic, zel]) => {
      const ler = (r: PromiseSettledResult<{ data: unknown }>, campo: string): number | null => {
        if (r.status !== 'fulfilled' || !r.value.data) return null;
        const d = r.value.data as Record<string, number>;
        return typeof d[campo] === 'number' ? d[campo] : null;
      };
      setNumeros({
        ouvidoria: ler(ouv, 'total'),
        esic: ler(esic, 'total_pedidos'),
        zeladoria: ler(zel, 'total'),
      });
    });
  }, []);

  const anoAtual = new Date().getFullYear();

  return (
    <Layout>
      {/* Hero */}
      <div className="bg-gradient-to-br from-institutional via-institutional-dark to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
          <h1 className="text-2xl sm:text-4xl font-bold leading-tight">{branding.nome}</h1>
          <p className="text-blue-100/90 mt-2 text-base sm:text-lg max-w-2xl">
            {branding.slogan || 'Portal do Cidadão — serviços, ouvidoria e transparência em um só lugar.'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 space-y-12">
        {/* Sem login */}
        <section>
          <SectionTitle icone={<FiCheckCircle size={16} />} titulo="Serviços sem login" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {semLogin.map((s) => (
              <CardLink key={s.titulo} {...s} />
            ))}
          </div>
        </section>

        {/* Vagas do conselho de usuários (Lei 13.460) — visível sem login; candidatura exige entrar */}
        {vagasConselho.length > 0 && (
          <section>
            <SectionTitle icone={<FiUsers size={16} />} titulo="Conselho de usuários · vagas abertas" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {vagasConselho.map((v) => (
                <Card key={v.id} className="h-full">
                  <CardContent>
                    <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-institutional/10 text-institutional"><FiUsers size={20} /></div>
                    <h3 className="text-base font-semibold text-slate-900">{v.titulo}</h3>
                    {v.descricao && <p className="mt-1 text-sm text-slate-600 line-clamp-2">{v.descricao}</p>}
                    <p className="mt-2 text-xs text-slate-400">
                      {v.total_vagas} vaga(s){v.encerra_em ? ` · inscrições até ${v.encerra_em.split('-').reverse().join('/')}` : ''}
                    </p>
                    <Link to="/conselho" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-institutional hover:underline">
                      Entrar para se candidatar <FiArrowRight size={14} />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">As vagas são públicas (transparência). A inscrição é identificada e exige login.</p>
          </section>
        )}

        {/* Transparência */}
        <section>
          <SectionTitle icone={<FiBarChart2 size={16} />} titulo={`Transparência · dados abertos ${anoAtual}`} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <KPI label="Manifestações na ouvidoria" valor={numeros.ouvidoria} />
            <KPI label="Pedidos de informação (e-SIC)" valor={numeros.esic} />
            <KPI label="Demandas de zeladoria" valor={numeros.zeladoria} />
            <KPI label="Total no ano" valor={somar(numeros)} destaque />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {transparencia.map((s) => (
              <CardLink key={s.titulo} {...s} compacto />
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function somar(n: Numeros): number | null {
  const vals = [n.ouvidoria, n.esic, n.zeladoria].filter((v): v is number => typeof v === 'number');
  if (vals.length === 0) return null;
  return vals.reduce((s, v) => s + v, 0);
}

function SectionTitle({ icone, titulo }: { icone: ReactNode; titulo: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-institutional">{icone}</span>
      <h2 className="text-sm uppercase tracking-wider text-slate-500 font-semibold">{titulo}</h2>
    </div>
  );
}

function CardLink({ titulo, descricao, href, icone, compacto, selo }: {
  titulo: string; descricao: string; href: string; icone: ReactNode; compacto?: boolean; selo?: string;
}) {
  return (
    <Link to={href} className="block group">
      <Card className="h-full transition hover:shadow-md hover:-translate-y-0.5">
        <CardContent className={compacto ? 'p-4' : ''}>
          <div className="flex items-start justify-between">
            <div className={`rounded-xl bg-institutional/10 text-institutional flex items-center justify-center ${compacto ? 'w-9 h-9 mb-2.5' : 'w-11 h-11 mb-3'}`}>
              {icone}
            </div>
            {selo && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                {selo}
              </span>
            )}
          </div>
          <h3 className={`font-semibold text-slate-900 ${compacto ? 'text-sm' : 'text-base'}`}>{titulo}</h3>
          <p className="text-sm text-slate-600 mt-1">{descricao}</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-institutional opacity-0 group-hover:opacity-100 transition">
            Acessar <FiArrowRight size={14} />
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}

function KPI({ label, valor, destaque }: { label: string; valor: number | null; destaque?: boolean }) {
  return (
    <Card className={destaque ? 'border-institutional/30 bg-institutional/[0.03]' : ''}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold text-slate-900 tabular-nums">
          {valor === null ? '—' : valor.toLocaleString('pt-BR')}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}
